import { inject, Injectable, Injector, type Type } from '@angular/core';

import type { ClarityDialogConfig } from '../models/clarity-dialog-config.model';
import { ClarityDialogRef } from './clarity-dialog-ref';

/**
 * Opens a component as a modal Clarity dialog.
 *
 * Service-opened, not host-mounted: a screen asks for a dialog where the decision is taken, and no
 * template has to carry an `<eff-…-dialog [open]>` element that is absent from the DOM most of the
 * time. The caller gets a {@link ClarityDialogRef} and never sees `MatDialogRef`, so the Material
 * coupling is one import in one library rather than one per ported screen.
 *
 * Named `ClarityDialog`, not `ClarityDialogService`: `@efficiently/client-ui-clarity-chrome` already
 * exports a `ClarityDialogService` and the two libraries coexist until the pulse slice is re-hosted.
 *
 * ## Which injector builds the body
 *
 * The {@link injector} below is the root environment injector, and it is the default *parent* of
 * the injector a dialog body is built in — enough for a body that reads only root-provided
 * services. A body that reads services bound on the screen that opened it needs that screen's
 * injector instead, which the caller passes through `ClarityDialogConfig.injector`. Nothing catches
 * a missing one at build time: it is a runtime resolution, and a body's own spec provides what it
 * needs into its TestBed root.
 */
@Injectable({ providedIn: 'root' })
export class ClarityDialog {
	private readonly injector = inject(Injector);

	/**
	 * Opens `content` as a modal and returns the handle to it.
	 *
	 * `R` is what the body closes with; `D` is the payload it reads from
	 * `CLARITY_OVERLAY_DATA_TOKEN`.
	 */
	public open<R = unknown, D = unknown>(
		content: Type<unknown>,
		config?: ClarityDialogConfig<D>,
	): ClarityDialogRef<R> {
		return new ClarityDialogRef<R>(config?.injector ?? this.injector, content, config);
	}
}
